import { cx } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import React from 'react'

import ProductInfoPriceDS from '@src/components/blocks/product-info-price-ds/product-info-price-ds'
import { ProductNameDS } from '@src/components/blocks/product-name-ds'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { IconButton } from '@src/quince-components/src/atoms/icon-button'
import { ProductTraditionalDiscountMessage } from '@src/quince-components/src/atoms/productTraditionalDiscountMessage'
import { PurchaseType } from '@src/quince-components/src/atoms/purchaseTypeSelector/PurchaseTypeSelector'
import Product from '@src/quince-site-common/src/domains/Product'
import type ProductTag from '@src/quince-site-common/src/domains/ProductTag'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import Heart from '@src/quince-site-common/src/organisms/appLayout/molecules/header/icons/heart.inline.svg'

import * as styles from './product-info-ds.styles'

export interface ProductInfoDSProps {
  product?: Product
  productInfoHeaderClassName?: string
  productInfoHeaderTextClassName?: string
  productTitle: string
  formattedSalePrice?: string
  hideSalePrice?: boolean
  formattedTraditionalPrice?: string
  isWishListed?: boolean
  className?: string
  avgRating?: number
  reviewCount?: number
  onWishListToggle?: (e: React.MouseEvent<HTMLElement>) => void
  hideWishListIcon?: boolean
  hideReviewSummary?: boolean
  onReviewSummaryClick?: (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void
  savingPercentage?: number
  brandName?: string
  brandLink?: string
  traditionalDiscountMessage?: string
  formattedFestivalOfferPrice?: string
  hideReviewCount?: boolean
  hideReviewLabel?: boolean
  productInfoReviewClassName?: string
  showDiscount?: boolean
  festivalPricePerUnit?: number
  subscriptionOfferPricePerUnit?: number
  pricePerServing?: number
  traditionalPricePerServing?: number
  hideSavings?: boolean
  isBundle?: boolean
  isFinalSaleProduct?: boolean
  isB2BFinalSaleProduct?: boolean
  formattedSubscriptionPrice?: string
  purchaseOption: PurchaseType
  updatedDesignForV2?: boolean
  containerRef?: React.RefObject<HTMLDivElement>
  isVariantThirdParty?: boolean
  selectedOptions?: ProductVariantOption[]
  /** SVD only — pre-scoped variant tags; see `ProductInfoPriceProps.svdVariantTags`. */
  svdVariantTags?: ProductTag[]
  /** STRFRNT-16780 details column: 24px desktop title→price gap (mobile and default stay 20px). */
  useDetailsColumnLayout?: boolean
}

const ProductInfoDS = ({
  product,
  productInfoHeaderClassName,
  productInfoHeaderTextClassName,
  productTitle,
  formattedSalePrice,
  hideSalePrice = false,
  formattedTraditionalPrice,
  isWishListed = false,
  className,
  avgRating,
  reviewCount,
  onWishListToggle,
  hideWishListIcon,
  hideReviewSummary,
  onReviewSummaryClick,
  savingPercentage,
  brandName,
  brandLink,
  traditionalDiscountMessage,
  formattedFestivalOfferPrice,
  hideReviewCount,
  hideReviewLabel,
  productInfoReviewClassName,
  showDiscount,
  festivalPricePerUnit,
  subscriptionOfferPricePerUnit,
  pricePerServing,
  traditionalPricePerServing,
  hideSavings,
  isBundle,
  isFinalSaleProduct,
  isB2BFinalSaleProduct,
  formattedSubscriptionPrice,
  purchaseOption,
  updatedDesignForV2 = false,
  containerRef,
  isVariantThirdParty,
  selectedOptions,
  svdVariantTags,
  useDetailsColumnLayout = false,
}: ProductInfoDSProps) => {
  const { t } = useTranslation('common')
  const STYLE_BLACK = 'black'
  const STYLE_WHITE = 'white'
  const STYLE_WIDTH = '20px'
  const STYLE_HEIGHT = '18px'
  const innerStyle = {
    // eslint-disable-next-line i18next/no-literal-string
    fill: isWishListed ? STYLE_BLACK : STYLE_WHITE,
    // eslint-disable-next-line i18next/no-literal-string
    width: STYLE_WIDTH,
    // eslint-disable-next-line i18next/no-literal-string
    height: STYLE_HEIGHT,
  }
  return (
    <div
      ref={containerRef}
      data-testid={TEST_LOCATORS.PDP_SECTIONS.PRODUCT_INFO}
      data-is-bundle={isBundle?.toString()}
      className={cx(
        styles.containerCss,
        className,
        // exactly one bottom-margin variant, most specific first
        isBundle
          ? styles.containerNewDesignV2BundleCss
          : isVariantThirdParty
            ? styles.containerThirdPartyCss
            : updatedDesignForV2
              ? styles.containerUpdatedDesignV2Css
              : styles.containerSpacingCss,
      )}
    >
      <div
        className={cx(
          useDetailsColumnLayout ? styles.containerHeaderColumnCss : styles.containerHeaderCss,
          productInfoHeaderClassName,
        )}
      >
        <ProductNameDS
          productTitle={productTitle}
          brandName={brandName}
          brandLink={brandLink}
          avgRating={avgRating}
          reviewCount={reviewCount}
          hideRating={hideReviewSummary}
          hideReviewCount={hideReviewCount}
          hideReviewLabel={hideReviewLabel}
          onReviewSummaryClick={onReviewSummaryClick}
          titleClassName={productInfoHeaderTextClassName}
          reviewCountClassName={productInfoReviewClassName}
          className={styles.productNameCss}
        />
        {!hideWishListIcon && (
          <IconButton
            className={styles.containerHeaderIconCss}
            iconSvg={Heart}
            innerStyle={innerStyle}
            onClick={onWishListToggle}
            ariaLabel={t('mark_product_as_favourite')}
            dataTestId={isWishListed ? TEST_LOCATORS.REMOVE_FROM_WISHLIST_BUTTON : TEST_LOCATORS.ADD_TO_WISHLIST_BUTTON}
          />
        )}
      </div>

      {!hideSalePrice && !!formattedSalePrice && (
        <ProductInfoPriceDS
          product={product}
          formattedSubscriptionPrice={formattedSubscriptionPrice}
          formattedSalePrice={formattedSalePrice}
          formattedTraditionalPrice={formattedTraditionalPrice}
          savingPercentage={savingPercentage}
          formattedFestivalOfferPrice={formattedFestivalOfferPrice}
          showDiscount={showDiscount}
          subscriptionOfferPricePerUnit={subscriptionOfferPricePerUnit}
          pricePerServing={pricePerServing}
          traditionalPricePerServing={traditionalPricePerServing}
          festivalPricePerUnit={festivalPricePerUnit}
          hideSavings={hideSavings}
          isFinalSaleProduct={isFinalSaleProduct}
          isB2BFinalSaleProduct={isB2BFinalSaleProduct}
          purchaseOption={purchaseOption}
          updatedDesignForV2={updatedDesignForV2}
          isVariantThirdParty={isVariantThirdParty}
          selectedOptions={selectedOptions}
          svdVariantTags={svdVariantTags}
          brandName={brandName}
        />
      )}

      <ProductTraditionalDiscountMessage
        formattedTraditionalPrice={formattedTraditionalPrice}
        traditionalDiscountMessage={traditionalDiscountMessage}
      />
    </div>
  )
}

export default ProductInfoDS
